import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { MapPin, Navigation, AlertCircle, Check, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface LocationPromptProps {
  isOpen: boolean;
  onClose: () => void;
}

const LocationPrompt: React.FC<LocationPromptProps> = ({ isOpen, onClose }) => {
  const { requestAndStoreCurrentLocation, currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const hasLocation = currentUser?.latitude && currentUser?.longitude;

  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleAllowLocation = async () => {
    setIsLoading(true);
    setError('');

    // Check if geolocation is supported
    if (!navigator.geolocation) {
      setError('Location is not supported by your browser. Please try using a different browser or updating your current one.');
      setIsLoading(false);
      return;
    }

    // First check permission state if available (helps detect if user already blocked it)
    if ('permissions' in navigator) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        if (permissionStatus.state === 'denied') {
          setError('Location access was previously blocked. To fix:\n\n1. Tap the menu icon (⋮) in your browser\n2. Tap "Site settings" or "Permissions"\n3. Find "Location" and set to "Allow"\n4. Come back and try again');
          setIsLoading(false);
          return;
        }
      } catch (e) {
        // Permission API not fully supported, continue anyway
        console.log('Permission query not supported, proceeding with request');
      }
    }

    try {
      const result = await requestAndStoreCurrentLocation();
      if (result) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 2000);
      } else {
        setError('Could not save location. Please ensure:\n\n1. Device location is ON (Settings → Location)\n2. You tapped "Allow" on the browser prompt\n3. You have an internet connection\n\nThen try again.');
      }
    } catch (err) {
      console.error('Error getting location:', err);
      if (err instanceof GeolocationPositionError) {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setError('Location access was blocked. To fix:\n\n1. Tap the menu icon (⋮) in your browser\n2. Tap "Site settings" or "Permissions"\n3. Find "Location" and set to "Allow"\n4. Come back and try again\n\nIf you blocked it accidentally, you need to reset the permission in your browser settings.');
            break;
          case err.POSITION_UNAVAILABLE:
            setError('Cannot detect your location. Please check:\n\n1. Device location is ON (Settings → Location)\n2. You have a stable internet connection\n3. You are not using a VPN that blocks location\n4. You are not in airplane mode\n\nThen try again.');
            break;
          case err.TIMEOUT:
            setError('Location request timed out. This usually means:\n\n• Device location is OFF (Settings → Location → Turn ON)\n• Poor GPS signal (try moving near a window)\n• Slow internet connection\n\nPlease enable device location and try again.');
            break;
          default:
            setError('Unable to access location. Please check:\n\n1. Device location is ON (Settings → Location)\n2. Browser has permission to access location\n3. You have an internet connection\n\nThen try again.');
        }
      } else {
        setError('Location services are not available. Please check:\n\n1. Device location is ON (Settings → Location)\n2. Browser has permission to access location\n3. You have an internet connection\n\nThen try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const portalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          <div
            className="nav-popover-backdrop"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="nav-popover-panel rounded-2xl border border-theme-cards bg-theme-cards shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <MapPin size={20} className="mr-2 text-secondary" />
                  <h2 className="text-lg font-semibold text-white">
                    {hasLocation ? 'Location Enabled' : 'Enable Location'}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-theme-text-secondary hover:text-white p-1"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              {hasLocation ? (
                <div>
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center mr-3">
                      <MapPin size={20} className="text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">
                        {currentUser?.location || 'My area'}
                      </p>
                      <p className="text-xs text-theme-text-secondary">
                        Current location
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-theme-text-secondary mb-4">
                    Your location is currently enabled. We use it to show you nearby Vybes and people.
                  </p>

                  {success ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-accent2/10 p-3 rounded-lg flex items-start"
                    >
                      <Check size={16} className="mr-2 mt-0.5 text-accent2 flex-shrink-0" />
                      <p className="text-accent2 text-sm">Location successfully updated!</p>
                    </motion.div>
                  ) : (
                    <button
                      onClick={handleAllowLocation}
                      disabled={isLoading}
                      className="w-full bg-secondary text-white py-3 rounded-full hover:bg-secondary-600 transition-colors flex items-center justify-center disabled:opacity-50 font-semibold"
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                          Updating Location...
                        </>
                      ) : (
                        <>
                          <Navigation size={16} className="mr-2" />
                          Update Location
                        </>
                      )}
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-sm text-theme-text-secondary mb-4">
                    Share your location to discover nearby Vybes and connect with people in your area.
                  </p>

                  {!error && !isLoading && (
                    <div className="mb-4 bg-blue-500/10 border border-blue-500/20 p-4 rounded-lg">
                      <div className="flex items-start mb-2">
                        <AlertCircle size={20} className="mr-2 mt-0.5 text-blue-400 flex-shrink-0" />
                        <p className="text-blue-400 text-sm font-semibold">Look for the browser prompt!</p>
                      </div>
                      <p className="text-blue-300 text-xs leading-relaxed">
                        After tapping the button below, your browser will show a permission request at the <span className="font-bold">top of the screen</span>. Make sure to tap <span className="font-bold">"Allow"</span> when you see it.
                      </p>
                    </div>
                  )}

                  {error && (
                    <div className="mb-4 bg-secondary/10 p-4 rounded-lg">
                      <div className="flex items-start mb-2">
                        <AlertCircle size={20} className="mr-2 mt-0.5 text-secondary flex-shrink-0" />
                        <p className="text-secondary text-sm font-semibold">Location Issue</p>
                      </div>
                      <p className="text-secondary text-sm whitespace-pre-line leading-relaxed">{error}</p>
                    </div>
                  )}

                  {success ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-accent2/10 p-3 rounded-lg flex items-start"
                    >
                      <Check size={16} className="mr-2 mt-0.5 text-accent2 flex-shrink-0" />
                      <p className="text-accent2 text-sm">Location successfully updated!</p>
                    </motion.div>
                  ) : (
                    <button
                      onClick={handleAllowLocation}
                      disabled={isLoading}
                      className="w-full bg-secondary text-white py-3 rounded-full hover:bg-secondary-600 transition-colors flex items-center justify-center disabled:opacity-50 font-semibold"
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                          Waiting for permission...
                        </>
                      ) : (
                        <>
                          <Navigation size={16} className="mr-2" />
                          Allow Location Access
                        </>
                      )}
                    </button>
                  )}

                  <p className="text-xs text-theme-text-secondary mt-4 text-center">
                    Your location is never shared with other users without your permission.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(portalContent, document.body);
};

export default LocationPrompt;
